import {
    BillingPlatform,
    DependenciesPayload,
    EligibilityOffer,
} from '@sxm-de/dev-kits/web-sdk/types';
import { MapClientSdkToWebSdk } from '@sxm-de/dev-kits/web-sdk/utils';

interface OffersNewRequest {
    promoCode?: string;
    country: 'US' | 'CA';
    serviceType: 'Automotive' | 'Streaming';
    stripePaymentMethodId?: string;
}

interface ProductGroupView {
    name: string;
    id: string;
    products: { id: string }[];
}

interface OffersNewResponse {
    offers: {
        offer: EligibilityOffer;
        campaignId?: string | null;
        productGroups: ProductGroupView[];
    }[];
    promoCodeStatus: {
        invalid: {
            reason?: 'Expired' | 'Invalid' | 'Redeemed' | 'Canceled';
        } | null;
        valid: {
            offersAffectTrialEligibility: boolean;
        };
    };
    fallbackOffers: boolean;
}

export async function getOffersNew(
    {
        promoCode,
        country,
        serviceType,
        stripePaymentMethodId,
    }: OffersNewRequest,
    { clientSdk }: DependenciesPayload,
): Promise<OffersNewResponse> {
    const mapClientSdkToWebSdk: MapClientSdkToWebSdk<
        Awaited<ReturnType<typeof clientSdk.commerce.eligibility.getOffersNew>>,
        OffersNewResponse
    > = ({ offers, promoCodeStatus, fallbackOffers }) => {
        return {
            offers: offers.map(({ offer, campaignId, productGroups }) => {
                let billingPlatform: BillingPlatform = 'Direct';
                if ('amazonAppStore' in offer.billingPlatform) {
                    billingPlatform = 'AmazonAppStore';
                } else if ('appleAppStore' in offer.billingPlatform) {
                    billingPlatform = 'AppleAppStore';
                } else if ('googlePlayStore' in offer.billingPlatform) {
                    billingPlatform = 'GooglePlayStore';
                } else if ('rokuPay' in offer.billingPlatform) {
                    billingPlatform = 'RokuPay';
                }
                return {
                    offer: {
                        ...offer,
                        name: offer.name || '',
                        products: offer.products.map((product) => {
                            return {
                                ...product,
                                productType: (product as any)
                                    .productType as Record<
                                    'partner' | 'main',
                                    unknown
                                >,
                            };
                        }),
                        billingPlatform,
                        basePrice: {
                            productAmount: {
                                value:
                                    offer.basePrice?.productAmount.value || 0,
                            },
                            total: {
                                value: offer.basePrice?.total.value || 0,
                            },
                        },
                        phases:
                            offer.phases?.map((phase) => {
                                return {
                                    ...phase,
                                    duration: {
                                        finite:
                                            'finite' in phase.duration
                                                ? phase.duration.finite
                                                : {
                                                      length: 0,
                                                      timeUnit: 'Months',
                                                  },
                                        infinite:
                                            'infinite' in phase.duration
                                                ? phase.duration.infinite
                                                : {},
                                    },
                                    billingPeriod: phase.billingPeriod || {
                                        length: 0,
                                        timeUnit: 'Months',
                                    },
                                    price: phase.price
                                        ? {
                                              ...phase.price,
                                              mrfAmount: phase.price
                                                  .mrfAmount || {
                                                  value: 0,
                                              },
                                              calculatedAs: {
                                                  basePrice: 0,
                                                  fixedDiscount: 0,
                                                  percentageDiscount: 0,
                                              },
                                          }
                                        : {
                                              productAmount: { value: 0 },
                                              total: { value: 0 },
                                              mrfAmount: { value: 0 },
                                              calculatedAs: {
                                                  basePrice: 0,
                                                  fixedDiscount: 0,
                                                  percentageDiscount: 0,
                                              },
                                          },
                                };
                            }) || [],
                    },
                    campaignId,
                    productGroups,
                };
            }),
            promoCodeStatus: {
                invalid:
                    promoCodeStatus && 'invalid' in promoCodeStatus
                        ? promoCodeStatus.invalid
                        : null,
                valid:
                    promoCodeStatus && 'valid' in promoCodeStatus
                        ? {
                              offersAffectTrialEligibility:
                                  promoCodeStatus.valid
                                      .offersAffectTrialEligibility || false,
                          }
                        : {
                              offersAffectTrialEligibility: false,
                          },
            },
            fallbackOffers: fallbackOffers || false,
        };
    };
    const offerData = mapClientSdkToWebSdk(
        await clientSdk.commerce.eligibility.getOffersNew({
            ...(promoCode ? { promoCode: promoCode } : {}),
            country,
            serviceType: serviceType,
            ...(stripePaymentMethodId ? { stripePaymentMethodId } : {}),
        }),
    );
    return offerData;
}
